import { useEffect, useState } from "react";

import Navbar from "../components/Navbar";
import DashboardChart from "../components/DashboardChart";

import api from "../services/api";
import { exportToExcel } from "../utils/exportExcel";

export default function BDODashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [blockId, setBlockId] = useState(null);
  const [blockName, setBlockName] = useState("");
  const [fromMonth, setFromMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  const [toMonth, setToMonth] = useState(new Date().toISOString().slice(0, 7));

  const [viewMode, setViewMode] = useState("month");

  const SESSION_LIMIT = 12 * 60 * 60 * 1000;

  useEffect(() => {
    const session = JSON.parse(sessionStorage.getItem("session"));

    if (!session) {
      window.location = "/login";
      return;
    }

    /* Session Expiry */

    const now = Date.now();

    if (now - session.loginTime > SESSION_LIMIT) {
      sessionStorage.clear();
      alert("Session expired. Please login again.");
      window.location = "/login";
      return;
    }

    if (!session.block_id) {
      setError("Block not assigned");
      setLoading(false);
      return;
    }

    setBlockId(session.block_id);
    setBlockName(session.block_name); 
    loadData(session.block_id);
  }, [fromMonth, toMonth, viewMode]);

  /* Load Data */

  const loadData = async (block) => {
    try {
      const res = await api.get(
        `/dashboard/block/${block}?from=${fromMonth}&to=${toMonth}&mode=${viewMode}`
      );

      setData(res.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError("Failed to load block data");
      setLoading(false);
    }
  };

  /* Summary */

  const totalSchemes = new Set(data.map((d) => d.scheme)).size;

  const totalDepartments = new Set(data.map((d) => d.department)).size;

  const totalValue = data.reduce((sum, row) => sum + Number(row.value), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto p-10">
        <h2 className="text-2xl font-bold mb-6">{blockName} - Block Monitoring Dashboard</h2>
        {/* Filters */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <div>
            <label className="text-sm text-gray-600">From Month</label>

            <input
              type="month"
              value={fromMonth}
              onChange={(e) => setFromMonth(e.target.value)}
              className="border p-2 rounded ml-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">To Month</label>

            <input
              type="month"
              value={toMonth}
              onChange={(e) => setToMonth(e.target.value)}
              className="border p-2 rounded ml-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">View Mode</label>

            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="border p-2 rounded ml-2"
            >
              <option value="month">Month Wise</option>

              <option value="cumulative">Cumulative</option>
            </select>
          </div>

          <button
            onClick={() => exportToExcel(data)}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Export Excel
          </button>
        </div>
        {/* Summary */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white shadow rounded p-6">
            <p className="text-gray-500">Schemes</p>
            <h3 className="text-3xl font-bold">{totalSchemes}</h3>
          </div>

          <div className="bg-white shadow rounded p-6">
            <p className="text-gray-500">Departments</p>
            <h3 className="text-3xl font-bold">{totalDepartments}</h3>
          </div>

          <div className="bg-white shadow rounded p-6">
            <p className="text-gray-500">Beneficiaries</p>
            <h3 className="text-3xl font-bold">{totalValue}</h3>
          </div>
        </div>
        {/* Chart */}
        <DashboardChart
          data={data}
          blockFilter={true}
          fromMonth={fromMonth}
          toMonth={toMonth}
        />
        {loading && <p className="text-gray-500">Loading dashboard...</p>}
        {error && <p className="text-red-600">{error}</p>}

        {!loading && !error && (
          <div className="mt-8 overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-3">Department</th>

                  <th className="p-3">Scheme</th>

                  <th className="p-3">Month</th>

                  <th className="p-3">Value</th>
                </tr>
              </thead>

              <tbody>
                {data.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-3">{row.department}</td>

                    <td className="p-3">{row.scheme}</td>

                    <td className="p-3">
                      {viewMode === "month"
                        ? row.entry_date
                          ? new Date(row.entry_date).toLocaleDateString(
                              "en-IN",
                              {
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : new Date(`${fromMonth}-01`).toLocaleDateString(
                              "en-IN",
                              {
                                month: "short",
                                year: "numeric",
                              }
                            )
                        : `${fromMonth} → ${toMonth}`}
                    </td>

                    <td className="p-3 font-semibold text-blue-700">
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
